import type { ExpiryTimeGranularity } from '@x-guard/xgac-types/xgac';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import {
  Button, Confirm, required, useTranslate, ValidationError,
} from 'react-admin';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { CertificationDocument, DocumentAssignee } from '../../../utils/certificationDocuments';
import type { CertificationsByAssetId, CertificationUserDetails } from '../../../utils/certificationUserForm';
import { TimeGranularityLabel } from '../../../utils/timeGranularityUtils';
import { CertificationDocumentPreviewButton } from './DocumentaryProof';
import { ExpiryDateDisplay, TABLE_EXPIRY_GRID_TEMPLATE } from './ExpiryDateDisplay';
import {
  CertificationDocumentInput,
  useCertificateAnalysisPending,
} from './inputs/CertificationDocumentInput';
import { GrayDateInputWithUpperLabel } from './inputs/GrayDateInputWithUpperLabel';
import { notLaterThan, notLaterThanToday } from '../../../utils/dateValidators';
import { DocumentProcessingSpinner } from './DocumentProcessingIndicators';

const datesDifferFromFirst = (recipients: DocumentAssignee[], certificationsByAssetId: CertificationsByAssetId | undefined, includeFirstIssuedOn: boolean) => {

  if (!certificationsByAssetId || recipients.length < 2) return false;

  const first = certificationsByAssetId[recipients[0].id];
  if (!first) return false;

  return recipients.slice(1).some(({ id }) => {

    const next = certificationsByAssetId[id];

    if (!next) return true;

    return (
      next.lastReceivedOn !== first.lastReceivedOn
      || next.expiryDate !== first.expiryDate
      || (includeFirstIssuedOn && next.firstIssuedOn !== first.firstIssuedOn)
    );

  });

};

const ApplyDatesToAllButton = (props: {
  recipients: DocumentAssignee[];
  includeFirstIssuedOn: boolean;
}) => {

  const { getValues, setValue, trigger } = useFormContext();

  const apply = async () => {

    const firstId = props.recipients[0]?.id;
    if (!firstId) return;

    const base = `certificationsByAssetId.${firstId}`;
    const fields = [
      ...(props.includeFirstIssuedOn ? [`${base}.firstIssuedOn`] : []),
      `${base}.lastReceivedOn`,
      `${base}.expiryDate`,
    ];

    const valid = await trigger(fields, { shouldFocus: true });
    if (!valid) return;

    const first = getValues(base) as CertificationUserDetails;

    props.recipients.slice(1).forEach(({ id }) => {

      const target = `certificationsByAssetId.${id}`;
      if (props.includeFirstIssuedOn) {

        setValue(`${target}.firstIssuedOn`, first.firstIssuedOn, {
          shouldDirty: false,
          shouldValidate: true,
        });

      }
      setValue(`${target}.lastReceivedOn`, first.lastReceivedOn, {
        shouldDirty: false,
        shouldValidate: true,
      });
      setValue(`${target}.expiryDate`, first.expiryDate, {
        shouldDirty: true,
        shouldValidate: true,
      });

    });

  };

  return (
    <Button
      type="button"
      onClick={apply}
      label="resources.assets.certifications.text.apply_dates_to_all"
      size="small"
      sx={{ whiteSpace: 'nowrap' }}
    />
  );

};

const NoPreviewPlaceholder = () => {

  const translate = useTranslate();

  return (
    <Box
      sx={{
        width: 48,
        height: 64,
        boxSizing: 'border-box',
        bgcolor: 'grey.400',
        color: 'common.white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 0.5,
        borderRadius: 0.5,
      }}
    >
      <Typography variant="caption" sx={{ fontSize: 9, lineHeight: 1.1 }}>
        {translate('resources.assets.certifications.text.no_preview')}
      </Typography>
    </Box>
  );

};

const RecipientNameCell = (props: {
  recipient: DocumentAssignee;
  isNew?: boolean;
  onRemoveRecipient?: (recipient: DocumentAssignee) => void;
}) => {

  const translate = useTranslate();
  const isDocumentAnalysisPending = useCertificateAnalysisPending();

  return (
    <TableCell sx={{ verticalAlign: 'middle', width: 200 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {props.onRemoveRecipient && (
          <Tooltip
            title={translate(
              'resources.certification-categories.text.remove_user_from_adding_tooltip',
            )}
          >
            <IconButton
              size="small"
              onClick={() => props.onRemoveRecipient?.(props.recipient)}
              disabled={isDocumentAnalysisPending}
              aria-label={translate(
                'resources.certification-categories.text.remove_user_from_adding_tooltip',
              )}
              sx={{ color: 'text.secondary', p: 0.5 }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        <Typography variant="body2" component="span">
          {props.recipient.label}
        </Typography>
        {props.isNew && (
          <Chip
            label={translate('resources.certification-categories.text.certified_users_new_badge')}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
      </Box>
    </TableCell>
  );

};

const CertificationUserRow = (props: {
  recipient: DocumentAssignee;
  timeGranularity: ExpiryTimeGranularity;
  includeFirstIssuedOn: boolean;
  requiresDocument: boolean;
  isFirstRow: boolean;
  isNew: boolean;
  recipients: DocumentAssignee[];
  showApplyToAll: boolean;
  onRemoveRecipient?: (recipient: DocumentAssignee) => void;
}) => {

  const translate = useTranslate();
  const { getFieldState, setValue, resetField } = useFormContext();
  const base = `certificationsByAssetId.${props.recipient.id}` as const;
  const firstIssuedSource = `${base}.firstIssuedOn`;
  const lastReceivedSource = `${base}.lastReceivedOn` as const;
  const expiryDateSource = `${base}.expiryDate` as const;
  const documentSource = `${base}.document` as const;

  const isDocumentAnalysisPending = useCertificateAnalysisPending(documentSource);

  const formState = useFormState({
    name: [
      firstIssuedSource,
      lastReceivedSource,
      expiryDateSource,
    ],
  });

  const rowError = [
    getFieldState(firstIssuedSource, formState).error,
    getFieldState(lastReceivedSource, formState).error,
    getFieldState(expiryDateSource, formState).error,
  ].find(Boolean);

  const document = useWatch({ name: documentSource }) as CertificationDocument | undefined;
  const hasDocument = Boolean(document?.rawFile || document?.src);

  const removeDocument = () => {

    setValue(documentSource, null, { shouldDirty: true, shouldValidate: false });
    resetField(lastReceivedSource);
    resetField(expiryDateSource);

  };

  return (
    <>
      {props.requiresDocument && (
        // row is hidden instead of being unmounted because the consent dialog is a part of the CertificationDocumentInput
        // which must stay mounted to show the dialog after the document is selected
        <TableRow sx={{ height: 80, display: hasDocument ? 'none' : 'table-row' }}>
          <RecipientNameCell
            recipient={props.recipient}
            isNew={props.isNew}
            onRemoveRecipient={props.onRemoveRecipient}
          />
          <TableCell sx={{ verticalAlign: 'middle', width: 104 }}>
            <NoPreviewPlaceholder />
          </TableCell>
          <TableCell colSpan={props.includeFirstIssuedOn ? 3 : 2}>
            <CertificationDocumentInput
              source={documentSource}
              lastReceivedOnSource={lastReceivedSource}
              expiryDateSource={expiryDateSource}
              label={false}
              validate={required()}
              labelSingle={translate(
                'resources.assets.certifications.text.drop_document_to_upload',
              )}
              sx={{ mb: 0 }}
            />
          </TableCell>
        </TableRow>
      )}
      {(!props.requiresDocument || hasDocument) && (
        <>
          <TableRow sx={{
            height: 80,
            ...(rowError?.message && {
              '& > td': {
                borderBottom: 'none',
              },
            }),
          }}>
            <RecipientNameCell
              recipient={props.recipient}
              isNew={props.isNew}
              onRemoveRecipient={props.onRemoveRecipient}
            />

            {props.requiresDocument && (
              <TableCell sx={{
                verticalAlign: 'middle',
                width: 104,
              }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: 48,
                    height: 64,
                  }}
                >
                  <CertificationDocumentPreviewButton source={documentSource} />
                  <Tooltip title={translate('ra.input.image.remove_current')}>
                    <IconButton
                      size="small"
                      onClick={removeDocument}
                      disabled={isDocumentAnalysisPending}
                      aria-label={translate('ra.input.image.remove_current')}
                      sx={{
                        position: 'absolute',
                        top: -7,
                        right: -7,
                        width: 22,
                        height: 22,
                        color: 'error.main',
                        bgcolor: 'background.paper',
                        boxShadow: 1,
                        zIndex: 1,
                        '&:hover': {
                          bgcolor: 'background.paper',
                          color: 'error.dark',
                        },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            )}

            {props.includeFirstIssuedOn && (
              <TableCell sx={{ verticalAlign: 'middle', width: 166, px: 1 }}>
                <GrayDateInputWithUpperLabel
                  source={firstIssuedSource}
                  label={false}
                  validate={[
                    required(),
                    notLaterThanToday(
                      'resources.assets.certifications.validation.first_issued_in_future',
                    ),
                    notLaterThan(
                      lastReceivedSource,
                      'resources.assets.certifications.validation.first_issued_later_than_last_received',
                    ),
                  ]}
                  helperText={false}
                  size="small"
                  sx={{
                    '& .MuiFormHelperText-root': {
                      display: 'none',
                    },
                  }}
                />
              </TableCell>
            )}

            <TableCell sx={{ verticalAlign: 'middle' }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: 'fit-content',
              }}>
                <ExpiryDateDisplay
                  startDateSource={lastReceivedSource}
                  expiryDateSource={`${base}.expiryDate`}
                  timeGranularity={props.timeGranularity}
                  minDateSource={props.includeFirstIssuedOn ? firstIssuedSource : undefined}
                  usage="table"
                  hideFieldErrors
                  isProcessing={isDocumentAnalysisPending}
                  startDateValidate={notLaterThanToday(
                    'resources.assets.certifications.validation.last_received_in_future',
                  )}
                />
                {isDocumentAnalysisPending && (
                  <Stack
                    role="status"
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      marginLeft: 2,
                      whiteSpace: props.includeFirstIssuedOn ? 'normal' : 'nowrap',
                    }}
                  >
                    <DocumentProcessingSpinner />
                    <Typography variant="body2">
                      {translate('resources.assets.certifications.text.ai_document_analysis_processing')}
                    </Typography>
                  </Stack>
                )}
                {props.isFirstRow && props.showApplyToAll && !isDocumentAnalysisPending && (
                  <ApplyDatesToAllButton
                    recipients={props.recipients}
                    includeFirstIssuedOn={props.includeFirstIssuedOn}
                  />
                )}
              </Box>
            </TableCell>
          </TableRow>

          {rowError?.message && (
            <TableRow>
              <TableCell
                colSpan={
                  3
                  + Number(props.requiresDocument)
                  + Number(props.includeFirstIssuedOn)
                }
                sx={{
                  color: 'error.main',
                  fontSize: 12,
                  pt: 0,
                  pb: 1,
                  borderTop: 0,
                }}
              >
                <ValidationError error={rowError.message} />
              </TableCell>
            </TableRow>
          )}
        </>
      )}
    </>
  );

};

export const CertificationUserTable = (props: {
  recipients: DocumentAssignee[];
  newRecipientIds?: Set<string>;
  timeGranularity: ExpiryTimeGranularity;
  includeFirstIssuedOn: boolean;
  requiresDocument: boolean;
  onRemoveRecipient?: (recipient: DocumentAssignee) => void;
}) => {

  const translate = useTranslate();
  const { getValues, setValue, unregister } = useFormContext();
  const [userToRemove, setUserToRemove] = useState<DocumentAssignee | null>(null);

  const confirmRemoveRecipient = () => {

    if (!userToRemove) return;

    const removedId = userToRemove.id;
    unregister([
      `certificationsByAssetId.${removedId}.firstIssuedOn`,
      `certificationsByAssetId.${removedId}.lastReceivedOn`,
      `certificationsByAssetId.${removedId}.expiryDate`,
      `certificationsByAssetId.${removedId}.document`,
    ]);
    const certifications = {
      ...((getValues('certificationsByAssetId') ?? {}) as CertificationsByAssetId),
    };

    delete certifications[removedId];
    setValue('certificationsByAssetId', certifications, { shouldDirty: true });

    setUserToRemove(null);
    props.onRemoveRecipient?.(userToRemove);

  };

  const certificationsByAssetId = useWatch({
    name: 'certificationsByAssetId',
  }) as CertificationsByAssetId | undefined;

  const hasAnyDateRow = !props.requiresDocument
    || props.recipients.some((recipient) => {

      const document = certificationsByAssetId?.[recipient.id]?.document;
      return Boolean(document?.rawFile || document?.src);

    });

  if (props.recipients.length === 0) {

    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: 'center', py: 4 }}
      >
        {translate('resources.certification-categories.text.certified_users_dialog_empty')}
      </Typography>
    );

  }

  return (
    <>
      <TableContainer>
        <Table
          size="small"
          sx={{
            width: '100%',
            minWidth: props.includeFirstIssuedOn ? 900 : 760,
            tableLayout: 'fixed',
          }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 200 }}>
                {translate('general.fields.name')}
              </TableCell>
              {props.requiresDocument && (
                <TableCell
                  sx={{
                    width: 104,
                  }}
                >
                  {translate('resources.assets.certifications.fields.documentary_proof')}
                </TableCell>
              )}
              {props.includeFirstIssuedOn && (
                <TableCell sx={{ width: 166, px: 1, textAlign: 'center' }}>
                  {hasAnyDateRow
                    ? translate(
                      'resources.assets.certifications.fields.first_issued_on',
                    )
                    : null}
                </TableCell>
              )}
              <TableCell>
                {hasAnyDateRow && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: TABLE_EXPIRY_GRID_TEMPLATE,
                      columnGap: 2,
                      width: 'fit-content',
                    }}
                  >
                    <Typography variant="body2" fontWeight={500} textAlign="center">
                      {translate(
                        'resources.assets.certifications.fields.last_received_on',
                      )}
                    </Typography>

                    <Chip
                      label={TimeGranularityLabel(
                        props.timeGranularity,
                        translate,
                      )}
                      sx={{ visibility: 'hidden' }}
                    />

                    <Typography
                      variant="body2"
                      fontWeight={500}
                      textAlign="center"
                    >
                      {translate(
                        'resources.assets.certifications.fields.expiry_date',
                      )}
                    </Typography>
                  </Box>
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.recipients.map((recipient, index) => (
              <CertificationUserRow
                key={recipient.id}
                recipient={recipient}
                timeGranularity={props.timeGranularity}
                includeFirstIssuedOn={props.includeFirstIssuedOn}
                requiresDocument={props.requiresDocument}
                isFirstRow={index === 0}
                isNew={props.newRecipientIds?.has(recipient.id) ?? false}
                recipients={props.recipients}
                showApplyToAll={datesDifferFromFirst(props.recipients, certificationsByAssetId, props.includeFirstIssuedOn)}
                onRemoveRecipient={
                  props.onRemoveRecipient
                    ? (r) => setUserToRemove(r)
                    : undefined
                }
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Confirm
        isOpen={Boolean(userToRemove)}
        title="resources.certification-categories.text.remove_user_from_adding_title"
        content={translate(
          'resources.certification-categories.text.remove_user_from_adding_content',
          { name: userToRemove?.label ?? '' },
        )}
        onClose={() => setUserToRemove(null)}
        onConfirm={confirmRemoveRecipient}
      />
    </>
  );

};
